import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { FirmwareItem } from '../../../types/otaUpdate';
import { CopyUrlButton } from '../CopyUrlButton';
import { COMPONENT_TYPES, modelOptionLabel } from '../firmwareForm';
import { formatFileSize } from '../otaOverview/otaData';
import { FirmwareDetailState } from './useFirmwareDetail';

interface SectionProps {
  firmware: FirmwareItem;
  state: FirmwareDetailState;
}

/** A labelled cell of the detail grid: the control while editing, the value otherwise. */
const Field: React.FC<{ label: string; editing: boolean; control?: React.ReactNode; full?: boolean; children: React.ReactNode }> = ({ label, editing, control, full, children }) => (
  <div className={`detail-item${full ? ' detail-item-full' : ''}`}>
    <label>{label}</label>
    {editing && control ? control : children}
  </div>
);

export const FirmwareIdentitySection: React.FC<SectionProps> = ({ firmware, state }) => {
  const { t } = useI18n();
  const { form, editing } = state;
  return (
    <div className="detail-section">
      <h3 className="section-title">{t('otaUpdates.firmwareIdentity')}</h3>
      <div className="detail-grid">
        <Field label={t('otaUpdates.model')} editing={editing} control={(
          <select value={form.model} onChange={(e) => state.setField('model', e.target.value)} className="field-select">
            {state.models.map((model) => <option key={model.code} value={model.code}>{modelOptionLabel(model)}</option>)}
          </select>
        )}>
          <span className="detail-value">{firmware.model}</span>
        </Field>
        <Field label={t('otaUpdates.componentType')} editing={editing} control={(
          <select value={form.type} onChange={(e) => state.setField('type', e.target.value)} className="field-select">
            {COMPONENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        )}>
          <span className="detail-value type-badge">{firmware.type}</span>
        </Field>
        <Field label={t('otaUpdates.version')} editing={editing} control={(
          <input type="text" data-testid="field-firmware-version-edit" value={form.version} onChange={(e) => state.setField('version', e.target.value)} className="field-input" />
        )}>
          <span className="detail-value version-value">{firmware.version}</span>
        </Field>
        <Field label={t('otaUpdates.fromVersion')} editing={editing} control={(
          <input type="text" data-testid="field-firmware-from-version-edit" value={form.from_version} onChange={(e) => state.setField('from_version', e.target.value)} className="field-input" placeholder={t('otaUpdates.anyVersion')} />
        )}>
          <span className="detail-value">{firmware.from_version || t('otaUpdates.anyVersion')}</span>
        </Field>
      </div>
    </div>
  );
};

export const FirmwareFileSection: React.FC<SectionProps> = ({ firmware, state }) => {
  const { t } = useI18n();
  const { form, editing } = state;
  return (
    <div className="detail-section">
      <h3 className="section-title">{t('otaUpdates.fileInformation')}</h3>
      <div className="detail-grid">
        <Field label={t('otaUpdates.fileUrl')} editing={editing} full control={(
          <div className="url-input-group">
            <input type="text" data-testid="field-firmware-file-url-edit" value={form.file_url} onChange={(e) => state.setField('file_url', e.target.value)} className="field-input" />
            {form.file_url && <CopyUrlButton url={form.file_url} />}
          </div>
        )}>
          <div className="url-display-group">
            <span className="detail-value url-value">{firmware.file_url}</span>
            {firmware.file_url && <CopyUrlButton url={firmware.file_url} />}
          </div>
        </Field>
        <Field label={t('otaUpdates.fileSize')} editing={editing} control={(
          <input type="text" data-testid="field-firmware-file-size-edit" value={form.file_size} onChange={(e) => state.setField('file_size', e.target.value)} className="field-input" placeholder={t('otaUpdates.fileSizePlaceholder')} />
        )}>
          <span className="detail-value">{formatFileSize(firmware.file_size)}</span>
        </Field>
        <Field label={t('otaUpdates.checksumSha256')} editing={editing} control={(
          <input type="text" data-testid="field-firmware-checksum-edit" value={form.checksum} onChange={(e) => state.setField('checksum', e.target.value)} className="field-input" />
        )}>
          <span className="detail-value checksum-value">{firmware.checksum || '-'}</span>
        </Field>
      </div>
    </div>
  );
};

export const FirmwareDeploymentSection: React.FC<SectionProps> = ({ firmware, state }) => {
  const { t } = useI18n();
  const { form, editing } = state;
  return (
    <div className="detail-section">
      <h3 className="section-title">{t('otaUpdates.deployment')}</h3>
      <div className="detail-grid">
        <Field label={t('clients.client')} editing={editing} control={(
          <select value={form.client_id} onChange={(e) => state.setField('client_id', e.target.value)} className="field-select">
            <option value="">{t('otaUpdates.allClientsGlobal')}</option>
            {state.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
          </select>
        )}>
          <span className="detail-value">{state.clientName(firmware.client_id)}</span>
        </Field>
        <Field label={t('otaUpdates.status')} editing={editing} control={(
          <label className="checkbox-label">
            <input type="checkbox" data-testid="field-firmware-is-active" checked={form.is_active} onChange={(e) => state.setField('is_active', e.target.checked)} />
            <span>{t('otaUpdates.activeForOta')}</span>
          </label>
        )}>
          <span className={`detail-value status-value ${firmware.is_active ? 'active' : 'inactive'}`}>{firmware.is_active ? t('common.active') : t('common.inactive')}</span>
        </Field>
        <Field label={t('otaUpdates.changelog')} editing={editing} full control={(
          <textarea value={form.changelog} onChange={(e) => state.setField('changelog', e.target.value)} className="field-textarea" rows={3} />
        )}>
          <span className="detail-value changelog-value">{firmware.changelog || t('otaUpdates.noChangelog')}</span>
        </Field>
      </div>
    </div>
  );
};

export const FirmwareMetadataSection: React.FC<{ firmware: FirmwareItem }> = ({ firmware }) => {
  const { formatDateTime, t } = useI18n();
  return (
    <div className="detail-section">
      <h3 className="section-title">{t('otaUpdates.metadata')}</h3>
      <div className="detail-grid">
        <div className="detail-item"><label>{t('clients.created')}</label><span className="detail-value">{formatDateTime(firmware.created_at)}</span></div>
        <div className="detail-item"><label>{t('otaUpdates.lastUpdated')}</label><span className="detail-value">{formatDateTime(firmware.updated_at)}</span></div>
        <div className="detail-item"><label>{t('otaUpdates.id')}</label><span className="detail-value">{firmware.id}</span></div>
      </div>
    </div>
  );
};
